import React, { useEffect, useMemo, useState } from 'react';
import { Flag, Plus, Trash2, ToggleLeft, ToggleRight, Search, Users2, Filter } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiDelete, apiFetch, apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';

const EMPTY_FORM = {
  name: '',
  description: '',
  enabled: false,
  target_scope: 'all',
  target_user_ids: '',
};

export default function AdminFeatureFlags() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    fetchFlags();
  }, []);

  const fetchFlags = async () => {
    try {
      const data = await apiGet(API_ROUTES.admin.featureFlags);
      setFlags(data.flags || []);
    } catch (error) {
      toast.error('Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  };

  const createFlag = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return toast.error('Flag name required');
    try {
      const res = await apiFetch(API_ROUTES.admin.featureFlags, {
        method: 'POST',
        body: form,
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Feature flag created');
      setForm(EMPTY_FORM);
      setShowForm(false);
      fetchFlags();
    } catch (error) {
      toast.error('Failed to create flag');
    }
  };

  const toggleFlag = async (flag) => {
    try {
      const res = await apiFetch(`${API_ROUTES.admin.featureFlags}/${flag.id}`, {
        method: 'PATCH',
        body: {
          enabled: !flag.enabled,
          target_scope: flag.target_scope,
          target_user_ids: flag.target_user_ids,
        },
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`Flag ${!flag.enabled ? 'enabled' : 'disabled'}`);
      fetchFlags();
    } catch (error) {
      toast.error('Failed to toggle flag');
    }
  };

  const deleteFlag = async (id) => {
    try {
      await apiDelete(`${API_ROUTES.admin.featureFlags}/${id}`);
      toast.success('Flag deleted');
      fetchFlags();
    } catch (error) {
      toast.error('Failed to delete flag');
    }
  };

  const normalizedSearch = search.trim().toLowerCase();
  const visibleFlags = useMemo(() => flags.filter((flag) => {
    const matchesSearch = !normalizedSearch
      || flag.name?.toLowerCase().includes(normalizedSearch)
      || flag.description?.toLowerCase().includes(normalizedSearch);
    const matchesScope = scopeFilter === 'all' || flag.target_scope === scopeFilter;
    return matchesSearch && matchesScope;
  }), [flags, normalizedSearch, scopeFilter]);

  const enabledCount = flags.filter((flag) => flag.enabled).length;
  const specificCount = flags.filter((flag) => flag.target_scope === 'specific').length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-100">Feature Flags</h1>
          <p className="text-sm text-gray-500 mt-1">Enable or disable features for users or plans</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-all text-sm font-medium"
        >
          <Plus size={14} />
          New Flag
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <SummaryCard icon={Flag} label="Total Flags" value={flags.length} sub={`${enabledCount} enabled right now`} />
        <SummaryCard icon={Filter} label="Plan Targeted" value={flags.filter((flag) => flag.target_scope === 'free' || flag.target_scope === 'pro').length} sub="Free or Pro scoped rules" />
        <SummaryCard icon={Users2} label="Specific Users" value={specificCount} sub="User-targeted rollouts" />
      </div>

      <div className="glass-card bg-dark-card p-4 mb-6 flex flex-col lg:flex-row lg:items-center gap-3 lg:justify-between">
        <div className="relative w-full lg:max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search flags by name or description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 bg-dark-bg border border-dark-border rounded-lg text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-red-500/50"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {['all', 'free', 'pro', 'specific'].map((scope) => (
            <FilterChip
              key={scope}
              active={scopeFilter === scope}
              onClick={() => setScopeFilter(scope)}
              label={scope}
            />
          ))}
        </div>
      </div>

      {showForm && (
        <form onSubmit={createFlag} className="glass-card bg-dark-card p-5 mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Flag name (e.g. dark_mode_v2)"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="px-4 py-2.5 bg-dark-bg border border-dark-border rounded-lg text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-red-500/50"
            />
            <select
              value={form.target_scope}
              onChange={(e) => setForm({ ...form, target_scope: e.target.value })}
              className="px-4 py-2.5 bg-dark-bg border border-dark-border rounded-lg text-sm text-gray-200 focus:outline-none focus:border-red-500/50"
            >
              <option value="all">All Users</option>
              <option value="free">Free Only</option>
              <option value="pro">Pro Only</option>
              <option value="specific">Specific Users</option>
            </select>
          </div>
          <input
            type="text"
            placeholder="Description..."
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full px-4 py-2.5 bg-dark-bg border border-dark-border rounded-lg text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-red-500/50"
          />
          {form.target_scope === 'specific' && (
            <input
              type="text"
              placeholder="User IDs (comma-separated)"
              value={form.target_user_ids}
              onChange={(e) => setForm({ ...form, target_user_ids: e.target.value })}
              className="w-full px-4 py-2.5 bg-dark-bg border border-dark-border rounded-lg text-sm text-gray-200 placeholder-gray-600 focus:outline-none focus:border-red-500/50"
            />
          )}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                className="rounded border-dark-border"
              />
              Start enabled
            </label>
            <button
              type="submit"
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all text-sm font-medium"
            >
              Create Flag
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : visibleFlags.length === 0 ? (
          <div className="text-center py-12 text-gray-500 bg-dark-card border border-dark-border rounded-xl">
            No feature flags match the current filters
          </div>
        ) : (
          visibleFlags.map((flag) => (
            <div key={flag.id} className="glass-card bg-dark-card p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <Flag size={16} className={flag.enabled ? 'text-green-400 mt-0.5' : 'text-gray-600 mt-0.5'} />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-gray-200 font-mono">{flag.name}</span>
                      <span className="px-1.5 py-0.5 rounded text-[9px] bg-dark-bg border border-dark-border text-gray-500 uppercase">
                        {flag.target_scope}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase ${flag.enabled ? 'bg-green-500/20 text-green-400' : 'bg-gray-700 text-gray-500'}`}>
                        {flag.enabled ? 'enabled' : 'disabled'}
                      </span>
                    </div>
                    {flag.description && <p className="text-xs text-gray-500 mt-1">{flag.description}</p>}
                    {flag.target_scope === 'specific' && flag.target_user_ids && (
                      <p className="text-[11px] text-gray-600 mt-2">Users: {flag.target_user_ids}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => toggleFlag(flag)}
                    className={`p-1.5 rounded-md transition-all ${flag.enabled ? 'text-green-400 hover:bg-red-500/10 hover:text-red-400' : 'text-gray-600 hover:bg-green-500/10 hover:text-green-400'}`}
                    title={flag.enabled ? 'Disable' : 'Enable'}
                  >
                    {flag.enabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                  </button>
                  <button
                    onClick={() => deleteFlag(flag.id)}
                    className="p-1.5 text-gray-600 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-all"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="glass-card bg-dark-card p-4">
      <div className="flex items-center gap-2 text-gray-500">
        <Icon size={14} />
        <span className="text-[11px] uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-2xl font-bold text-gray-100 mt-2">{value}</p>
      <p className="text-xs text-gray-600 mt-1">{sub}</p>
    </div>
  );
}

function FilterChip({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition ${
        active
          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
          : 'bg-dark-surface text-gray-400 border border-dark-border hover:text-gray-200'
      }`}
    >
      {label}
    </button>
  );
}
